import React from 'react';
import { Target, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatCurrency, formatNumber } from '../../../utils/analysis.utils';
import { Tooltip } from '../../../../../shared/components/Tooltip';

interface EnergySavingsMaximumCardProps {
  analysis: any;
  configuration?: any;
}

export const EnergySavingsMaximumCard: React.FC<EnergySavingsMaximumCardProps> = ({
  analysis,
  configuration
}) => {
  const { t } = useTranslation();

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
      <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50 rounded-t-2xl">
        <h3 className="text-base font-semibold text-black flex items-center gap-3">
          <div className="w-8 h-8 rounded-xl bg-gray-100 flex items-center justify-center">
            <Target className="w-4 h-4 text-brand-primary" />
          </div>
          {t('optimizationAnalysis.theoretical.energySavingsCard.title')}
        </h3>
      </div>
      <div className="p-6">
        <div className="space-y-4">
          <div className="text-center">
            <p className="text-3xl font-bold text-brand-primary">
              {formatCurrency(analysis.theoretical.maximum)}
            </p>
            <div className="text-sm text-gray-600 mt-1">
              {t('optimizationAnalysis.theoretical.energySavingsCard.of')}{' '}
              <Tooltip
                position="bottom"
                content={
                  <div className="text-xs text-gray-700 space-y-2 max-w-xs">
                    <p className="font-semibold text-gray-900 mb-2">{t('optimizationAnalysis.theoretical.energySavingsCard.tooltipTitle')}</p>
                    <div className="space-y-2">
                      <div className="bg-gray-50 p-2 rounded">
                        <p className="font-medium">{t('optimizationAnalysis.theoretical.energySavingsCard.method1Title')}</p>
                        <p className="text-gray-600">{t('optimizationAnalysis.theoretical.energySavingsCard.method1Formula')}</p>
                        <p className="font-bold">{formatCurrency(analysis.totalAnnualEnergyChargeBill)}</p>
                        <p className="text-xs text-gray-500 mt-1">{t('optimizationAnalysis.theoretical.energySavingsCard.method1Source')}</p>
                      </div>
                      <div className="bg-gray-50 p-2 rounded">
                        <p className="font-medium">{t('optimizationAnalysis.theoretical.energySavingsCard.method2Title')}</p>
                        <p className="text-gray-600">
                          {formatNumber(analysis.totalConsumption / 1000, 0)} MWh × ${formatNumber(analysis.retailTariff, 0)}/MWh
                        </p>
                        <p className="font-bold">{formatCurrency(analysis.totalAnnualEnergyChargeCalc)}</p>
                        <p className="text-xs text-gray-500 mt-1">{t('optimizationAnalysis.theoretical.energySavingsCard.method2Source')}</p>
                      </div>
                    </div>
                    {analysis.totalAnnualEnergyChargeBill && analysis.totalAnnualEnergyChargeCalc &&
                      Math.abs(analysis.totalAnnualEnergyChargeBill - analysis.totalAnnualEnergyChargeCalc) > 1000 && (
                      <div className="mt-2 p-2 bg-gray-50 rounded border border-gray-200">
                        <p className="text-xs text-gray-800">
                          <strong>{t('optimizationAnalysis.theoretical.energySavingsCard.noteLabel')}</strong> {t('optimizationAnalysis.theoretical.energySavingsCard.differenceDetected', { amount: formatCurrency(Math.abs(analysis.totalAnnualEnergyChargeBill - analysis.totalAnnualEnergyChargeCalc)) })}
                          {analysis.totalAnnualEnergyChargeCalc > analysis.totalAnnualEnergyChargeBill
                            ? t('optimizationAnalysis.theoretical.energySavingsCard.higherUsageNote')
                            : t('optimizationAnalysis.theoretical.energySavingsCard.higherChargesNote')}
                          {t('optimizationAnalysis.theoretical.energySavingsCard.usingConsumptionMethod', { amount: formatCurrency(analysis.totalAnnualEnergyChargeCalc) })}
                        </p>
                      </div>
                    )}
                  </div>
                }
              >
                <span className="underline decoration-dotted cursor-help">
                  {formatCurrency(analysis.totalAnnualEnergyCharge)} {t('optimizationAnalysis.theoretical.energySavingsCard.totalEnergyCharge')}
                </span>
              </Tooltip>
            </div>
            <p className="text-lg font-semibold text-gray-900">
              {formatNumber((analysis.theoretical.maximum / analysis.totalAnnualEnergyCharge) * 100, 1)}% {t('optimizationAnalysis.theoretical.energySavingsCard.reduction')}
            </p>
            <p className="text-sm text-gray-500 mt-2">{t('optimizationAnalysis.theoretical.energySavingsCard.annualSavingsPerfect')}</p>

            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 bg-gray-100 rounded-full">
              <Info className="w-4 h-4 text-gray-600" />
              <span className="text-xs text-gray-700 font-medium">
                {t('optimizationAnalysis.theoretical.energySavingsCard.basedOnDataPercent', { percent: formatNumber(analysis.valueOpportunities.length / 8760 * 100, 0) })}
              </span>
            </div>

            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-600 font-semibold mb-1">{t('optimizationAnalysis.theoretical.energySavingsCard.calculationDetails')}</p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="text-left">
                  <span className="text-gray-500">{t('optimizationAnalysis.theoretical.energySavingsCard.totalConsumption')}</span>
                  <span className="ml-1 font-medium">{formatNumber(analysis.totalConsumption)} kWh</span>
                </div>
                <div className="text-left">
                  <span className="text-gray-500">{t('optimizationAnalysis.theoretical.energySavingsCard.gridCapacity')}</span>
                  <span className="ml-1 font-medium">
                    {formatNumber(analysis.gridCapacityKw)} kW
                    {!configuration?.grid?.maxConnectionKw && <span className="text-xs text-gray-400"> {t('optimizationAnalysis.theoretical.energySavingsCard.peakIndicator')}</span>}
                  </span>
                </div>
                <div className="text-left">
                  <span className="text-gray-500">{t('optimizationAnalysis.theoretical.energySavingsCard.baselineCost')}</span>
                  <span className="ml-1 font-medium">{formatCurrency(analysis.theoretical.baselineCost)}</span>
                </div>
                <div className="text-left">
                  <span className="text-gray-500">{t('optimizationAnalysis.theoretical.energySavingsCard.optimizedCost')}</span>
                  <span className="ml-1 font-medium">{formatCurrency(analysis.theoretical.theoreticalCost)}</span>
                </div>
                <div className="text-left">
                  <span className="text-gray-500">{t('optimizationAnalysis.theoretical.energySavingsCard.totalMwh')}</span>
                  <span className="ml-1 font-medium">{formatNumber(analysis.totalConsumption / 1000, 1)} MWh</span>
                </div>
                <div className="text-left">
                  <span className="text-gray-500">{t('optimizationAnalysis.theoretical.energySavingsCard.maxHourly')}</span>
                  <span className="ml-1 font-medium">{formatNumber(analysis.maxHourlyConsumption, 1)} kWh</span>
                </div>
                <div className="text-left">
                  <span className="text-gray-500">{t('optimizationAnalysis.theoretical.energySavingsCard.avgHourly')}</span>
                  <span className="ml-1 font-medium">{formatNumber(analysis.totalConsumption / Math.max(1, analysis.valueOpportunities?.length || 8760), 1)} kWh</span>
                </div>
              </div>
              <div className="mt-2 pt-2 border-t border-gray-200">
                <div className="text-left text-xs">
                  <span className="text-gray-500">{t('optimizationAnalysis.theoretical.energySavingsCard.costBreakdown')}</span>
                </div>
                <div className="text-left text-xs mt-1">
                  <span className="text-gray-500">{t('optimizationAnalysis.theoretical.energySavingsCard.currentRetail')}</span>
                  <span className="ml-1 font-medium">{formatCurrency(analysis.totalAnnualEnergyCharge)}</span>
                </div>
                <div className="text-left text-xs">
                  <span className="text-gray-500">{t('optimizationAnalysis.theoretical.energySavingsCard.theoreticalShifted')}</span>
                  <span className="ml-1 font-medium">{formatCurrency(analysis.totalAnnualEnergyCharge - analysis.theoretical.maximum)}</span>
                </div>
                <div className="text-left text-xs">
                  <span className="text-gray-500">{t('optimizationAnalysis.theoretical.energySavingsCard.savings')}</span>
                  <span className="ml-1 font-medium text-brand-primary">{formatCurrency(analysis.theoretical.maximum)}</span>
                  <span className="ml-1 text-gray-700">({formatNumber((analysis.theoretical.maximum / analysis.totalAnnualEnergyCharge) * 100, 1)}%)</span>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">{t('optimizationAnalysis.theoretical.energySavingsCard.hoursNeeded')}</span>
              <span className="font-medium">
                {formatNumber(analysis.theoretical.hoursNeeded)} / {formatNumber(analysis.hoursInYear)}
                <span className="text-gray-500 ml-1">({formatNumber(analysis.theoretical.percentOfYear, 1)}%)</span>
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">{t('optimizationAnalysis.theoretical.energySavingsCard.kwhShifted')}</span>
              <span className="font-medium">
                {formatNumber(analysis.totalConsumption, 0)} / {formatNumber(analysis.totalConsumption, 0)}
                <span className="text-gray-500 ml-1">(100%)</span>
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">{t('optimizationAnalysis.theoretical.energySavingsCard.hoursBelowRetail')}</span>
              <span className="font-medium">{formatNumber(analysis.hoursBelowRetail)} {t('units.hours')}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">{t('optimizationAnalysis.theoretical.energySavingsCard.avgValueCaptured')}</span>
              <span className="font-medium">{formatCurrency(analysis.theoretical.avgValuePerMWh)}/MWh</span>
            </div>
          </div>

          <div className="bg-gray-50 p-3 rounded-lg">
            <p className="text-xs text-gray-700">
              {t('optimizationAnalysis.theoretical.energySavingsCard.perfectShiftingNote', {
                mwh: formatNumber(analysis.totalConsumption / 1000, 1),
                hours: formatNumber(analysis.theoretical.hoursNeeded),
                mw: formatNumber(analysis.maxHourlyConsumption / 1000, 2)
              })}
            </p>
            {analysis.theoretical.hoursNeeded > analysis.hoursBelowRetail && (
              <p className="text-xs text-gray-600 mt-1">
                {t('optimizationAnalysis.theoretical.energySavingsCard.noteMoreHours', {
                  moreHours: formatNumber(analysis.theoretical.hoursNeeded - analysis.hoursBelowRetail),
                  belowRetail: formatNumber(analysis.hoursBelowRetail),
                  retailPrice: formatNumber(analysis.retailTariff, 0)
                })}
              </p>
            )}
            <p className="text-xs text-gray-600 mt-1">
              {t('optimizationAnalysis.theoretical.energySavingsCard.peakConstrainedNote')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};